import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { SettingsSection } from "component/shared/settings-page";
import { RefreshTable } from "component/shared/refresh-table";
import { Column, Sort } from "component/shared/table";
import { Tag } from "component/shared/tag";
import { User } from "model/user";
import { BillingInvoiceResource } from "api/resource/billing";
import * as api from "api/api";
import { fetchBillingInvoices } from "redux/slice/billing";
import { RootState, useAppThunkDispatch } from "redux/store";
import { compareIssueDates, formatBillingDate, formatMoney, invoiceStatusTag } from "./billing-format";
import { useBillingStyles } from "./styles";

interface Props {
	user: User;
}

const SORT: Sort = { id: "issueDate", desc: true };

export const Invoices: React.FC<Props> = ({ user }) => {
	const { classes } = useBillingStyles();
	const dispatch = useAppThunkDispatch();
	const error = useSelector((state: RootState) => state.billing.invoicesError);
	const invoices = useSelector((state: RootState) => state.billing.invoices);
	const [downloading, setDownloading] = useState<string | null>(null);
	const [downloadFailed, setDownloadFailed] = useState(false);

	useEffect(() => {
		dispatch(fetchBillingInvoices());
	}, [dispatch]);

	const columns = useMemo<Column<BillingInvoiceResource>[]>(() => {
		const download = (invoice: BillingInvoiceResource) => {
			setDownloading(invoice.displayNumber);
			api.downloadBillingInvoice(invoice.displayNumber)
				.catch(() => setDownloadFailed(true))
				.finally(() => setDownloading(null));
		};
		return [
			{
				id: "displayNumber",
				title: "Invoice #",
				value: (invoice) => invoice.displayNumber,
			},
			{
				id: "issueDate",
				title: "Issue Date",
				value: (invoice) => formatBillingDate(invoice.issueDate) || "—",
				sort: compareIssueDates,
			},
			{
				id: "dueDate",
				title: "Due Date",
				value: (invoice) => formatBillingDate(invoice.dueDate) || "—",
			},
			{
				id: "status",
				title: "Status",
				value: (invoice) => {
					const tag = invoiceStatusTag(invoice.status);
					return <Tag label={tag.label} variant={tag.variant} size="small" />;
				},
			},
			{
				id: "total",
				title: "Total",
				value: (invoice) => formatMoney(invoice.total, invoice.currency),
			},
			{
				id: "download",
				align: "right",
				value: (invoice) => (
					<Mui.IconButton
						color="primary"
						aria-label={`Download invoice ${invoice.displayNumber}`}
						disabled={downloading === invoice.displayNumber}
						onClick={() => download(invoice)}
					>
						<Icons.Download size={20} />
					</Mui.IconButton>
				),
			},
		];
	}, [downloading]);

	let body: React.ReactNode;
	if (!invoices && error) {
		body = <Mui.Alert severity="error">We couldn't load your invoices.</Mui.Alert>;
	} else if (!invoices) {
		body = <Mui.Skeleton variant="rectangular" height={200} className={classes.skeleton} />;
	} else if (!invoices.length) {
		body = <Mui.Typography className={classes.value}>No invoices yet.</Mui.Typography>;
	} else {
		body = (
			<RefreshTable
				items={invoices}
				columns={columns}
				sort={SORT}
				rowKey={(invoice) => invoice.displayNumber}
				paginate
				userId={user.agentId}
				tableKey="invoices"
			/>
		);
	}

	return (
		<SettingsSection title="Invoices">
			{body}
			<Mui.Snackbar open={downloadFailed} autoHideDuration={4000} onClose={() => setDownloadFailed(false)}>
				<MuiAlert severity="error" onClose={() => setDownloadFailed(false)}>
					We couldn't download this invoice. Please try again later.
				</MuiAlert>
			</Mui.Snackbar>
		</SettingsSection>
	);
};
